// src/GaloBlogPost.jsx - enhanced with dynamic images in related articles and improved markdown
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowRight, 
  ChevronRight,
  ArrowUp, 
  Mail, 
  CheckCircle,
  Linkedin,
  Facebook,
  Calendar,
  Clock,
  MessageCircle,
  Bookmark,
  ThumbsUp,
  Twitter,
  Link as LinkIcon,
  ChevronUp,
  ChevronDown,
  Tag,
  Share2,
  Sparkles,
  Copy,
  ExternalLink
} from 'lucide-react';

// Import layout components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Import our blog utilities
import { getBlogPostBySlug, getRelatedPosts } from './data/blogPosts';
import { MarkdownContent, useMarkdownContent } from './utils/markdownLoader';
import { setDocumentMeta, setOpenGraphMeta } from './utils/documentMeta';

// 🎯 Import the dynamic blog image component
import DynamicBlogImage from './components/sections/DynamicBlogImage';

const GaloBlogPost = () => {
  // Get the blog post slug from URL params
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [scrolled, setScrolled] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [showTableOfContents, setShowTableOfContents] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  
  // Get blog post data
  const post = getBlogPostBySlug(slug);
  const relatedPosts = post ? getRelatedPosts(post.id, post.category, 3) : [];
  
  // Load markdown content
  const { content, toc, loading, error } = useMarkdownContent(post?.contentFile);
  
  // Set document title and meta tags
  useEffect(() => {
    if (post) {
      setDocumentMeta(
        `${post.title} | Galo Industries Blog`,
        post.excerpt
      );
      
      setOpenGraphMeta({
        title: post.title,
        description: post.excerpt,
        image: post.image,
        type: 'article',
        url: window.location.href
      });
    }
    
    return () => {
      setDocumentMeta('Galo Industries', 'Industry insights and automation trends');
    };
  }, [post]);
  
  // Redirect if post not found
  useEffect(() => {
    if (!post && !loading) {
      navigate('/blog', { replace: true });
    }
  }, [post, loading, navigate]);

  // Handle scroll for reading progress and active sections
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Calculate reading progress
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      setReadingProgress(Math.min(scrolled, 100));
      
      // Update active section on scroll
      if (toc.length > 0) {
        const sections = toc.map(item => document.getElementById(item.id));
        const scrollPosition = window.scrollY + 150;
        
        for (let i = sections.length - 1; i >= 0; i--) {
          const section = sections[i];
          if (section && section.offsetTop <= scrollPosition) {
            setActiveSection(toc[i].id);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [toc]);
  
  // Newsletter form
  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    console.log('Newsletter signup:', newsletterEmail);
    setNewsletterEmail('');
    alert('Thank you for subscribing to our newsletter!');
  };
  
  // Scroll to section
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      window.scrollTo({
        top: section.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };

  // Share functionality
  const shareUrl = window.location.href;
  const shareTitle = post?.title || '';

  const handleShare = (platform) => {
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      copy: () => {
        navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      }
    };

    if (platform === 'copy') {
      urls.copy();
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  // If post is not found or still loading
  if (!post || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block p-4 rounded-full bg-blue-50 mb-4">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h2 className="text-xl font-medium text-slate-700">Loading article...</h2>
        </div>
      </div>
    );
  }

  // If there was an error loading the post
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-lg">
          <div className="inline-block p-4 rounded-full bg-red-50 mb-4">
            <svg className="h-8 w-8 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-medium text-slate-700 mb-2">Error Loading Article</h2>
          <p className="text-slate-600">{error}</p>
          <Link to="/blog" className="mt-6 inline-block px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Return to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans antialiased overflow-x-hidden bg-white">
      {/* Reading Progress Bar */}
      <div 
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-cyan-500 to-blue-600 z-50 transition-all duration-300"
        style={{ width: `${readingProgress}%` }}
      />

      {/* Enhanced Global Styles for Better Reading Experience */}
      <style jsx global>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); opacity: 0.8; }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse 4s ease-in-out infinite;
        }
        
        .table-of-contents li {
          margin-bottom: 0.5rem;
        }
        
        /* Enhanced Markdown Styling for Better Readability */
        .prose-container {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          color: #334155;
          line-height: 1.8;
          font-size: 1.125rem;
        }
        
        .prose-container h1 {
          font-size: 2.5rem;
          font-weight: 800;
          margin-top: 0;
          margin-bottom: 2rem;
          color: #0f172a;
          letter-spacing: -0.02em;
          line-height: 1.2;
        }
        
        .prose-container h2 {
          font-size: 2rem;
          font-weight: 700;
          margin-top: 3rem;
          margin-bottom: 1.5rem;
          color: #1e293b;
          letter-spacing: -0.025em;
          scroll-margin-top: 6rem;
          position: relative;
          padding-bottom: 0.75rem;
        }
        
        .prose-container h2::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(90deg, #0891b2, #1d4ed8);
          border-radius: 2px;
        }
        
        .prose-container h3 {
          font-size: 1.625rem;
          font-weight: 700;
          margin-top: 2.5rem;
          margin-bottom: 1.25rem;
          color: #334155;
          letter-spacing: -0.025em;
          scroll-margin-top: 6rem;
        }
        
        .prose-container h4 {
          font-size: 1.375rem;
          font-weight: 600;
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: #475569;
          letter-spacing: -0.025em;
          scroll-margin-top: 6rem;
        }
        
        .prose-container h5 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.75rem;
          margin-bottom: 0.75rem;
          color: #64748b;
          letter-spacing: -0.025em;
        }
        
        .prose-container p {
          margin-bottom: 1.75rem;
          color: #475569;
          line-height: 1.8;
          font-size: 1.125rem;
        }
        
        .prose-container .lead {
          font-size: 1.25rem;
          color: #334155;
          font-weight: 500;
          line-height: 1.7;
          margin-bottom: 2rem;
        }
        
        .prose-container ul, .prose-container ol {
          margin-top: 1.5rem;
          margin-bottom: 1.5rem;
          padding-left: 2rem;
        }
        
        .prose-container ul {
          list-style-type: none;
        }
        
        .prose-container ul li::before {
          content: "▸";
          color: #0891b2;
          font-weight: bold;
          display: inline-block;
          width: 1em;
          margin-left: -1em;
        }
        
        .prose-container ol {
          list-style-type: decimal;
          list-style-position: outside;
        }
        
        .prose-container ol li {
          padding-left: 0.5rem;
        }
        
        .prose-container li {
          margin-top: 0.75rem;
          margin-bottom: 0.75rem;
          color: #475569;
          line-height: 1.75;
          font-size: 1.125rem;
        }
        
        .prose-container strong {
          font-weight: 600;
          color: #1e293b;
        }
        
        .prose-container em {
          font-style: italic;
          color: #64748b;
        }
        
        .prose-container blockquote {
          margin: 2rem 0;
          padding: 1.5rem 2rem;
          border-left: 4px solid #0891b2;
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f7fa 100%);
          border-radius: 0 0.75rem 0.75rem 0;
          font-style: italic;
          color: #334155;
          font-size: 1.125rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        
        .prose-container blockquote p {
          margin: 0;
          color: #334155;
        }
        
        .prose-container blockquote cite {
          display: block;
          margin-top: 1rem;
          font-style: normal;
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 500;
        }
        
        .prose-container a {
          color: #0891b2;
          text-decoration: underline;
          text-decoration-color: #22d3ee;
          text-underline-offset: 3px;
          text-decoration-thickness: 2px;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        
        .prose-container a:hover {
          color: #1d4ed8;
          text-decoration-color: #3b82f6;
          background-color: #f0f9ff;
          padding: 2px 4px;
          border-radius: 4px;
          margin: -2px -4px;
        }
        
        .prose-container code:not(pre code) {
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          color: #dc2626;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          font-family: 'JetBrains Mono', 'Fira Code', 'Monaco', 'Cascadia Code', 'Roboto Mono', monospace;
          font-size: 0.9em;
          font-weight: 600;
          border: 1px solid #e2e8f0;
        }
        
        .prose-container pre {
          margin: 2rem 0;
          border-radius: 0.75rem;
          overflow: hidden;
          background: #0f172a;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          border: 1px solid #1e293b;
        }
        
        .prose-container pre code {
          background: transparent;
          color: #10b981;
          padding: 1.5rem;
          display: block;
          font-family: 'JetBrains Mono', 'Fira Code', 'Monaco', 'Cascadia Code', 'Roboto Mono', monospace;
          font-size: 0.875rem;
          line-height: 1.7;
          overflow-x: auto;
        }
        
        .prose-container img {
          border-radius: 0.75rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          margin: 2.5rem 0;
          width: 100%;
          height: auto;
          border: 1px solid #e2e8f0;
        }
        
        .prose-container figure {
          margin: 2.5rem 0;
        }
        
        .prose-container figcaption {
          margin-top: 1rem;
          text-align: center;
          font-size: 0.875rem;
          color: #64748b;
          font-style: italic;
        }
        
        .prose-container hr {
          margin: 3rem 0;
          border: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
        }
        
        .prose-container table {
          width: 100%;
          margin: 2.5rem 0;
          border-collapse: collapse;
          border-radius: 0.75rem;
          overflow: hidden;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
        }
        
        .prose-container th {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 1rem 1.5rem;
          text-align: left;
          font-weight: 600;
          color: #1e293b;
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.05em;
          border-bottom: 2px solid #e2e8f0;
        }
        
        .prose-container td {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f1f5f9;
          color: #475569;
          font-size: 1rem;
        }
        
        .prose-container tr:nth-child(even) {
          background-color: #f8fafc;
        }
        
        .prose-container tr:hover {
          background-color: #f1f5f9;
        }
        
        /* Special content blocks */
        .prose-container .note,
        .prose-container .warning,
        .prose-container .tip,
        .prose-container .info {
          margin: 2rem 0;
          padding: 1.5rem;
          border-radius: 0.75rem;
          border-left: 4px solid;
          position: relative;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        
        .prose-container .note {
          background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
          border-left-color: #2563eb;
          color: #1e40af;
        }
        
        .prose-container .warning {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border-left-color: #f59e0b;
          color: #92400e;
        }
        
        .prose-container .tip {
          background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
          border-left-color: #10b981;
          color: #065f46;
        }
        
        .prose-container .info {
          background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
          border-left-color: #0891b2;
          color: #164e63;
        }
        
        /* Enhanced focus states for accessibility */
        .prose-container a:focus {
          outline: 2px solid #0891b2;
          outline-offset: 2px;
          border-radius: 4px;
        }
        
        /* Print styles */
        @media print {
          .prose-container {
            font-size: 12pt;
            line-height: 1.6;
            color: black;
          }
          
          .prose-container a {
            color: black;
            text-decoration: underline;
          }
          
          .prose-container pre {
            background: #f5f5f5;
            border: 1px solid #ddd;
          }
          
          .prose-container code {
            background: #f5f5f5;
            color: black;
          }
        }
      `}</style>
      
      {/* Use Header component from layout */}
      <Header activeSection="blog" />
      
      {/* Blog Post Hero Section - Keep existing hero as requested */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-cyan-600 via-blue-700 to-blue-900 text-white relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-[radial-gradient(circle,_rgba(255,255,255,0.15)_1px,_transparent_1px)] bg-[length:20px_20px]"></div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-cyan-300/10 to-blue-300/5 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-blue-500/10 to-indigo-300/5 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
        
        {/* Gradient border bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-r from-cyan-500 to-blue-600 opacity-20"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          {/* Breadcrumb */}
          <div className="mb-8">
            <div className="flex items-center text-blue-200 text-sm">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-white font-medium">{post.category}</span>
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {/* Category Badge */}
            <div className="mb-4">
              <Link 
                to={`/blog/category/${post.category.toLowerCase()}`} 
                className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20 shadow-sm"
              >
                <span className="text-blue-100 font-medium text-sm">{post.category}</span>
              </Link>
            </div>
            
            {/* Post Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {post.title}
            </h1>
            {post.subtitle && (
              <p className="text-xl text-blue-100 mb-8">
                {post.subtitle}
              </p>
            )}
            
            {/* Post Metadata */}
            <div className="flex flex-wrap items-center gap-6 mb-10">
              <div className="flex items-center gap-4 text-blue-200 text-sm">
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {post.date}
                </span>
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {post.readTime}
                </span>
              </div>
            </div>
            
            {/* Enhanced Social Share */}
            <div className="hidden md:flex items-center justify-between py-4 border-b border-white/20">
              <div className="flex items-center gap-4">
                <span className="text-blue-100">Share this article:</span>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleShare('facebook')}
                    className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-gradient-to-r hover:from-cyan-500 hover:to-blue-600 transition-colors"
                  >
                    <Facebook className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleShare('twitter')}
                    className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-gradient-to-r hover:from-cyan-500 hover:to-blue-600 transition-colors"
                  >
                    <Twitter className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleShare('linkedin')}
                    className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-gradient-to-r hover:from-cyan-500 hover:to-blue-600 transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleShare('copy')}
                    className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-gradient-to-r hover:from-cyan-500 hover:to-blue-600 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setIsBookmarked(!isBookmarked)}
                  className={`flex items-center text-blue-100 hover:text-white transition-colors ${isBookmarked ? 'text-yellow-300' : ''}`}
                >
                  <Bookmark className={`w-4 h-4 mr-1 ${isBookmarked ? 'fill-current' : ''}`} />
                  <span>{isBookmarked ? 'Saved' : 'Save'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Main Content Section */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Article Content */}
            <div className="lg:col-span-8">
              {/* Article Introduction */}
              {post.excerpt && (
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-l-4 border-cyan-500 p-6 rounded-r-lg mb-8">
                  <p className="text-lg text-slate-700 font-medium leading-relaxed">
                    {post.excerpt}
                  </p>
                </div>
              )}

              {/* Render Enhanced Markdown content */}
              <article className="prose-container">
                <MarkdownContent contentFile={post.contentFile} />
              </article>
              
              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-slate-700 font-medium">Tags:</span>
                    {post.tags.map((tag, index) => (
                      <Link
                        key={index}
                        to={`/blog/tag/${tag.toLowerCase().replace(/\s+/g, '-')}`}
                        className="bg-slate-100 hover:bg-cyan-100 text-slate-700 hover:text-cyan-700 px-3 py-1 rounded-full text-sm transition-colors"
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Enhanced Mobile Social Share */}
              <div className="mt-8 pt-6 border-t border-slate-200 md:hidden">
                <div className="flex flex-col gap-4">
                  <h4 className="text-slate-900 font-medium">Share this article</h4>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => handleShare('facebook')}
                      className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white hover:shadow-md transition-all"
                    >
                      <Facebook className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleShare('twitter')}
                      className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white hover:shadow-md transition-all"
                    >
                      <Twitter className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleShare('linkedin')}
                      className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white hover:shadow-md transition-all"
                    >
                      <Linkedin className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleShare('copy')}
                      className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white hover:shadow-md transition-all"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* 🎯 Enhanced Related Articles with Dynamic Images */}
              {relatedPosts.length > 0 && (
                <div className="mt-20 pt-12 border-t border-slate-200">
                  {/* Section Header */}
                  <div className="text-center mb-12">
                    <div className="inline-flex items-center mb-4">
                      <div className="h-px w-12 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
                      <span className="text-cyan-700 font-semibold uppercase text-sm tracking-wider mx-3 flex items-center">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Related Articles
                      </span>
                      <div className="h-px w-12 bg-gradient-to-r from-blue-600 to-cyan-500"></div>
                    </div>
                    
                    <h3 className="text-3xl font-bold text-slate-900 mb-4">
                      Continue <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Learning</span>
                    </h3>
                    
                    <p className="text-slate-600 max-w-2xl mx-auto">
                      Explore more insights about {post.category.toLowerCase()} and industrial automation
                    </p>
                  </div>

                  {/* Related Posts Grid with Dynamic Images */}
                  <div className="grid md:grid-cols-3 gap-8">
                    {relatedPosts.map((relatedPost, index) => (
                      <div
                        key={relatedPost.id}
                        className="group bg-white rounded-xl overflow-hidden shadow-md border border-slate-100 flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-cyan-100"
                      >
                        {/* 🎯 Dynamic Image instead of static */}
                        <div className="relative overflow-hidden">
                          <DynamicBlogImage
                            post={relatedPost}
                            className="w-full h-48"
                          />
                          
                          {/* Category Badge */}
                          <div className="absolute top-4 left-4">
                            <span className="inline-block px-3 py-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-medium rounded-md shadow-sm">
                              {relatedPost.category}
                            </span>
                          </div>

                          {/* Featured Badge */}
                          {relatedPost.featured && (
                            <div className="absolute top-4 right-4">
                              <div className="px-2 py-1 bg-yellow-400 text-slate-900 text-xs font-bold rounded-md shadow-lg">
                                Featured
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="p-5 flex flex-col flex-grow">
                          <div className="flex items-center text-xs text-slate-500 mb-3 gap-3">
                            <span className="flex items-center">
                              <Calendar className="w-3.5 h-3.5 mr-1 text-cyan-600" />
                              {relatedPost.date}
                            </span>
                            <span className="flex items-center">
                              <Clock className="w-3.5 h-3.5 mr-1 text-cyan-600" />
                              {relatedPost.readTime}
                            </span>
                          </div>
                          
                          <h4 className="text-lg font-bold mb-2 text-slate-900 group-hover:text-cyan-700 transition-colors line-clamp-2 flex-grow">
                            <Link to={`/blog/${relatedPost.slug}`} className="block">
                              {relatedPost.title}
                            </Link>
                          </h4>
                          
                          <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                            {relatedPost.excerpt}
                          </p>
                          
                          <Link 
                            to={`/blog/${relatedPost.slug}`}
                            className="inline-flex items-center text-cyan-700 font-medium hover:text-blue-700 transition-colors group text-sm mt-auto"
                          >
                            Read Article
                            <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* View More Button */}
                  <div className="text-center mt-10">
                    <Link 
                      to={`/blog/category/${post.category.toLowerCase()}`}
                      className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-lg hover:shadow-blue-600/20 rounded-lg shadow-md transition-all font-medium"
                    >
                      More {post.category} Articles
                      <ArrowRight className="ml-3 w-5 h-5" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            {/* Sidebar */}
            <div className="lg:col-span-4">
              {/* Table of Contents */}
              {toc.length > 0 && (
                <div className="sticky top-24 bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden mb-8">
                  <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-cyan-500 to-blue-600">
                    <h3 className="font-bold text-white">Table of Contents</h3>
                    <button 
                      onClick={() => setShowTableOfContents(!showTableOfContents)}
                      className="text-white hover:bg-white/10 p-1 rounded"
                    >
                      {showTableOfContents ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                  </div>
                  
                  {showTableOfContents && (
                    <nav className="p-5 table-of-contents">
                      <ul className="space-y-1">
                        {toc.map((item) => (
                          <li key={item.id} style={{ paddingLeft: (item.level - 2) * 16 }}>
                            <a
                              href={`#${item.id}`}
                              onClick={(e) => {
                                e.preventDefault();
                                scrollToSection(item.id);
                              }}
                              className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                                activeSection === item.id
                                  ? 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 font-medium border-l-2 border-cyan-500'
                                  : 'text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              {item.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  )}
                </div>
              )}
              
              {/* Post Info Card */}
              <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden mb-8">
                <div className="p-5 border-b border-slate-200 bg-gradient-to-br from-slate-50 to-blue-50">
                  <h3 className="font-bold text-slate-900">Article Information</h3>
                </div>
                
                <div className="p-5">
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <Calendar className="w-5 h-5 text-cyan-700 mr-3 mt-0.5" />
                      <div>
                        <div className="text-sm text-slate-500 mb-1">Published On</div>
                        <div className="font-medium">{post.date}</div>
                      </div>
                    </li>
                    {post.lastUpdated && (
                      <li className="flex items-start">
                        <Clock className="w-5 h-5 text-cyan-700 mr-3 mt-0.5" />
                        <div>
                          <div className="text-sm text-slate-500 mb-1">Last Updated</div>
                          <div className="font-medium">{post.lastUpdated}</div>
                        </div>
                      </li>
                    )}
                    <li className="flex items-start">
                      <Tag className="w-5 h-5 text-cyan-700 mr-3 mt-0.5" />
                      <div>
                        <div className="text-sm text-slate-500 mb-1">Category</div>
                        <Link 
                          to={`/blog/category/${post.category.toLowerCase()}`} 
                          className="font-medium text-cyan-700 hover:text-blue-700 transition-colors"
                        >
                          {post.category}
                        </Link>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              
              {/* Newsletter signup */}
              <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                <div className="p-5 border-b border-slate-200 bg-gradient-to-br from-slate-50 to-blue-50">
                  <h3 className="font-bold text-slate-900">Subscribe to Our Newsletter</h3>
                </div>
                
                <div className="p-5">
                  <p className="text-slate-600 text-sm mb-4">
                    Stay updated with the latest insights, trends, and updates in industrial automation.
                  </p>
                  
                  <form onSubmit={handleNewsletterSubmit}>
                    <div className="mb-3">
                      <input
                        type="email"
                        value={newsletterEmail}
                        onChange={(e) => setNewsletterEmail(e.target.value)}
                        placeholder="Your email address"
                        className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors shadow-sm"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:shadow-lg hover:shadow-blue-600/20 text-white font-medium rounded-lg shadow-md transition-all"
                    >
                      Subscribe
                    </button>
                  </form>
                  
                  <div className="text-center mt-3 text-xs text-slate-500 flex items-center justify-center">
                    <CheckCircle className="w-3.5 h-3.5 mr-1 text-cyan-700" />
                    <span>We respect your privacy.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Newsletter Section */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-blue-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-br from-cyan-100/50 to-transparent rounded-bl-full opacity-60 transform translate-x-1/4 -translate-y-1/4"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tl from-blue-100/50 to-transparent rounded-tr-full opacity-60 transform -translate-x-1/4 translate-y-1/4"></div>
        
        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-xl p-6 sm:p-10 border border-slate-100 relative overflow-hidden newsletter-section">
            <div className="absolute inset-0 bg-[radial-gradient(#e1ebf8_1px,transparent_1px)] bg-[length:12px_12px] opacity-30 -z-10"></div>
            
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-white border border-slate-200 flex items-center justify-center text-cyan-700 mx-auto mb-4 shadow-md">
                <Mail className="w-8 h-8" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-slate-900">Stay Updated with <span className="text-cyan-700">Industry Trends</span></h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Subscribe to our newsletter for the latest automation insights, case studies, and technology updates
              </p>
            </div>
            
            <form onSubmit={handleNewsletterSubmit} className="max-w-xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors shadow-sm"
                  required
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:shadow-lg hover:shadow-blue-600/20 text-white font-medium rounded-xl shadow-md transition-all"
                >
                  Subscribe
                </button>
              </div>
              <div className="text-center mt-4 text-sm text-slate-500 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 mr-2 text-cyan-700" />
                We respect your privacy. Unsubscribe at any time.
              </div>
            </form>
          </div>
        </div>
      </section>
      
      <Footer />
      
      {/* Back to top button */}
      {scrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl shadow-lg flex items-center justify-center hover:-translate-y-1 transition-all duration-300 z-20 hover:shadow-xl hover:shadow-blue-600/20"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default GaloBlogPost;